import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { appsAPI, categoriesAPI } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import AppCard from '../components/app/AppCard';
import CustomDropdown from '../components/common/CustomDropdown';
import { Rocket, Loader, Trophy, TrendingUp } from 'lucide-react';

const HomePage = () => {
  const { isAuthenticated, openAuthModal } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [weeklyGroups, setWeeklyGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedL1Category, setSelectedL1Category] = useState('');
  const [selectedL2Category, setSelectedL2Category] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Calculate next Monday
  const getNextMonday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + daysUntilMonday);
    
    // Format as "MMM DD, YYYY"
    return nextMonday.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Check for category parameters in URL and auto-select
  useEffect(() => {
    const l1Param = searchParams.get('l1');
    const l2Param = searchParams.get('l2');
    
    if (l1Param) {
      setSelectedL1Category(l1Param);
    }
    if (l2Param) {
      setSelectedL2Category(l2Param);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [appsResponse, categoriesResponse] = await Promise.all([
        appsAPI.getHomepage(),
        categoriesAPI.getAll(),
      ]);
      setWeeklyGroups(appsResponse.data.weekly_groups);
      setCategories(categoriesResponse.data.categories);
    } catch (err) {
      setError('Failed to load apps. Please try again.');
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleL1Change = (value) => {
    setSelectedL1Category(value);
    setSelectedL2Category(''); // Reset L2 when L1 changes
  };

  const handleL2Change = (value) => {
    setSelectedL2Category(value);
  };

  // Get L2 subcategories for selected L1
  const subcategories = selectedL1Category
    ? categories.find(cat => cat.id === selectedL1Category)?.subcategories || []
    : [];

  // Filter apps based on selected categories - WITHIN each week
  const getFilteredWeeklyGroups = () => {
    if (!selectedL1Category && !selectedL2Category) {
      return weeklyGroups;
    }

    return weeklyGroups.map(weekGroup => {
      // Filter apps in this week
      const filteredWeekApps = weekGroup.apps.filter(app => {
        if (selectedL2Category) {
          return app.category_l2_id === selectedL2Category;
        } else if (selectedL1Category) {
          return app.category_l1_id === selectedL1Category;
        }
        return true;
      });

      // Return week with filtered apps, maintaining 6-app limit and has_more logic
      const displayApps = filteredWeekApps.slice(0, 6);
      const totalFiltered = filteredWeekApps.length;

      return {
        ...weekGroup,
        apps: displayApps,
        total_count: totalFiltered,
        has_more: totalFiltered > 6
      };
    }).filter(weekGroup => weekGroup.total_count > 0); // Hide weeks with no matching apps
  };

  const filteredWeeklyGroups = getFilteredWeeklyGroups();
  const currentWeekApps = filteredWeeklyGroups.length > 0 ? filteredWeeklyGroups[0].apps : [];
  const totalApps = weeklyGroups.reduce((sum, week) => sum + week.total_count, 0);
  const filteredTotalApps = filteredWeeklyGroups.reduce((sum, week) => sum + week.total_count, 0);

  const getFilterTitle = () => {
    if (selectedL2Category) {
      const l2Cat = subcategories.find(cat => cat.id === selectedL2Category);
      return l2Cat ? `${l2Cat.name}` : "Filtered Apps";
    } else if (selectedL1Category) {
      const l1Cat = categories.find(cat => cat.id === selectedL1Category);
      return l1Cat ? `${l1Cat.name}` : "Filtered Apps";
    }
    return "This Week's Top Apps";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-dark-bg">
        <Loader className="w-12 h-12 animate-spin text-brand-yellow" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Hero Section */}
      <div className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-transparent to-transparent"></div>
        
        <div className="relative max-w-4xl mx-auto px-8 sm:px-12 lg:px-16 pt-12 sm:pt-16 lg:pt-20 pb-6 sm:pb-8 lg:pb-10 text-center">
          {/* Weekly Contest Card - Now First */}
          <div className="flex flex-col items-center mb-6 sm:mb-8">
            <div className="inline-block w-full max-w-3xl">
              <div className="bg-purple-900/30 border border-purple-700/30 rounded-xl p-5 sm:p-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Trophy className="w-5 h-5 text-brand-yellow" />
                  <h3 className="text-lg font-bold text-white">Weekly Contest</h3>
                </div>
                <p className="text-center text-gray-300 text-sm sm:text-base mb-3">
                  Top 5 by upvotes win 500 credits || Contest starts {getNextMonday()}
                </p>
                <div className="text-center">
                  <Link
                    to={isAuthenticated ? '/submit' : '#'}
                    onClick={(e) => {
                      if (!isAuthenticated) {
                        e.preventDefault();
                        openAuthModal();
                      }
                    }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-brand-yellow text-dark-bg rounded-lg hover:bg-brand-yellow/90 transition-colors font-bold text-base"
                    data-testid="contest-submit-link"
                  >
                    Submit your app
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Title - Now Second */}
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-3 tracking-tight max-w-4xl mx-auto leading-tight">
            Collection of standout apps built with Emergent
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 sm:px-12 lg:px-16 pt-3 sm:pt-4 pb-12">
        {/* Category Dropdowns */}
        <div className="mb-8" data-testid="category-browse-section">
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span className="text-gray-400 text-sm font-medium">Categories</span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 max-w-2xl">
            {/* L1 Dropdown */}
            <CustomDropdown
              value={selectedL1Category}
              onChange={handleL1Change}
              options={[
                { value: '', label: 'All Categories' },
                ...categories.map(cat => ({ value: cat.id, label: cat.name }))
              ]}
              placeholder="All Categories"
              className="flex-1"
            />

            {/* L2 Dropdown */}
            <CustomDropdown
              value={selectedL2Category}
              onChange={handleL2Change}
              options={[
                { value: '', label: selectedL1Category ? 'All Subcategories' : 'Select category first' },
                ...subcategories.map(subcat => ({ value: subcat.id, label: subcat.name }))
              ]}
              placeholder={selectedL1Category ? 'All Subcategories' : 'Select category first'}
              disabled={!selectedL1Category}
              className="flex-1"
            />

            {/* Clear Filters Button */}
            {(selectedL1Category || selectedL2Category) && (
              <button
                onClick={() => {
                  setSelectedL1Category('');
                  setSelectedL2Category('');
                }}
                className="px-4 py-3 bg-dark-border text-gray-300 rounded-lg hover:bg-brand-yellow hover:text-dark-bg transition-colors font-medium text-sm whitespace-nowrap"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Section Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-2xl font-bold text-white">
              {filteredWeeklyGroups.length > 0 && `${filteredWeeklyGroups[0].week_label.replace('This Week: ', '').replace('Last Week: ', '')}`}
            </h2>
            {filteredWeeklyGroups.length > 0 && filteredWeeklyGroups[0].week_label.includes('This Week') && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-600 text-white rounded-full text-sm font-bold">
                <Trophy className="w-4 h-4" />
                Ongoing Contest
              </span>
            )}
          </div>
          {(selectedL1Category || selectedL2Category) && filteredWeeklyGroups.length > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              Showing {filteredWeeklyGroups[0].total_count} {filteredWeeklyGroups[0].total_count === 1 ? 'app' : 'apps'} in {getFilterTitle()}
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 p-4 bg-red-900/20 border border-red-700/30 rounded-lg">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Apps List View - Current Week */}
        {currentWeekApps.length === 0 ? (
          <div className="text-center py-20 bg-dark-card rounded-lg border border-dark-border">
            <p className="text-gray-500 text-lg">
              {selectedL1Category || selectedL2Category 
                ? 'No apps in this category yet' 
                : 'No apps submitted yet. Be the first!'}
            </p>
            {(selectedL1Category || selectedL2Category) && (
              <button
                onClick={() => {
                  setSelectedL1Category('');
                  setSelectedL2Category('');
                }}
                className="mt-4 text-brand-yellow hover:text-brand-yellow/90 font-medium"
              >
                View all apps
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {currentWeekApps.map((app) => (
                <AppCard key={app.id} app={app} />
              ))}
            </div>
            
            {/* See all button after cards if current week has more than 6 apps */}
            {filteredWeeklyGroups.length > 0 && filteredWeeklyGroups[0].has_more && (
              <div className="text-center mt-6">
                <Link
                  to={`/week/${filteredWeeklyGroups[0].week}`}
                  className="inline-block text-gray-400 hover:text-brand-yellow transition-colors font-medium text-sm"
                  data-testid="see-all-current-week"
                >
                  Show All ({filteredWeeklyGroups[0].total_count})
                </Link>
              </div>
            )}
          </>
        )}

        {/* Previous Weeks - All weeks with proper demarcation */}
        {filteredWeeklyGroups.slice(1).map((weekGroup) => (
          <div key={weekGroup.week} className="mt-16" data-testid="week-group">
            <h3 className="text-2xl font-bold text-white mb-6">
              {weekGroup.week_label.replace('This Week: ', '').replace('Last Week: ', '')}
            </h3>
            
            <div className="space-y-4">
              {weekGroup.apps.map((app) => (
                <AppCard key={app.id} app={app} />
              ))}
            </div>

            {/* See all button for this week if it has more than 6 apps */}
            {weekGroup.has_more && (
              <div className="text-center mt-6">
                <Link
                  to={`/week/${weekGroup.week}`}
                  className="inline-block text-gray-400 hover:text-brand-yellow transition-colors font-medium text-sm"
                  data-testid="see-all-button"
                >
                  Show All ({weekGroup.total_count})
                </Link>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default HomePage;
